import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isNative } from "@/lib/appConfig";

// Top-level sections reachable from BottomNav or the drawer.
// Back from any of these should minimize the app, not tunnel back into prior routes.
const ROOT_PATHS = new Set([
    "/",
    "/login",
    "/app/dashboard",
    "/app/portfolios",
    "/app/holdings",
    "/app/transactions",
    "/app/dividends",
    "/app/benchmarks",
    "/app/alerts",
    "/app/fiscal",
    "/app/reports",
    "/app/settings",
    "/app/billing",
    "/app/help",
    "/app/referrals",
    "/app/import",
    "/app/connections",
]);

// Handles the Android hardware back button: close an open dialog first, else close
// the drawer, else go back in history — and only minimize the app at a root screen.
// Without this, back exits the app from any screen.
export default function NativeBackButton() {
    const navigate = useNavigate();

    useEffect(() => {
        if (!isNative()) return;
        let remove = () => {};
        (async () => {
            const { App } = await import("@capacitor/app");
            const handle = await App.addListener("backButton", ({ canGoBack }) => {
                // 1. Close any open Radix overlay (Dialog / AlertDialog / Sheet).
                const openOverlay = document.querySelector(
                    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
                );
                if (openOverlay) {
                    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
                    return;
                }
                // 2. Close the mobile drawer before navigating back.
                if (document.querySelector('[data-testid="mobile-drawer"][data-open="true"]')) {
                    window.dispatchEvent(new Event("qc:closeDrawer"));
                    return;
                }
                // 3. Navigate back if not at a root section.
                const path = window.location.pathname;
                if (canGoBack && !ROOT_PATHS.has(path)) {
                    navigate(-1);
                } else {
                    App.minimizeApp();
                }
            });
            remove = () => handle.remove();
        })();
        return () => remove();
    }, [navigate]);

    return null;
}
